<script setup>
import { computed, reactive, ref, watch } from 'vue';
import {
    buildActionableSuggestions,
    buildDisplayRows,
    calculateFlatTotals,
    calcMonthlyAmortization,
    formatMonthlyAmortization,
    formatFlatAmountInput,
    isPolicyLoanOption,
    isTouchedDraft,
    normalizeFlatAmountInput,
    shouldShowEmptyCategoryHint,
    suggestedPolicyLoanAmount,
    sumCellInteractionMode,
    validateFlatDraft
} from '@/utils/flatBalanceSheetEditor';

const props = defineProps({
    types: { type: Array, default: () => [] }, rows: { type: Array, default: () => [] },
    classMap: { type: Object, default: () => ({}) }, optionsByType: { type: Object, default: () => ({}) },
    fieldsByOption: { type: Object, default: () => ({}) }, relations: { type: Array, default: () => [] },
    readonly: Boolean, seedPlaceholders: Boolean, saveLoading: Boolean,
    saveLabel: { type: String, default: '儲存草稿' },
    showSaveBar: { type: Boolean, default: true }
});
const emit = defineEmits(['save-all']);
const drafts = ref([]); const deletedIds = ref([]); const errors = reactive({});
const oid = (o) => Number(o?.value ?? o?.id);
const olabel = (o) => o?.label ?? o?.name ?? '';
const optionFor = (d) => (props.optionsByType[d.type_id] || []).find((o) => oid(o) === Number(d.option_id));
const isEmptyValue = (value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
const flattenValues = (fields = [], out = {}) => { fields.forEach((f) => { const id = Number(f.field_id ?? f.id); if (id) out[id] = f.value ?? ''; flattenValues([...(f.fields || []), ...(f.children || [])], out); }); return out; };
const makeDraft = (row, typeId) => {
    const hasAmount = Object.prototype.hasOwnProperty.call(row, 'amount');
    const hasMonthly = Object.prototype.hasOwnProperty.call(row, 'monthly_interest_expense');
    return {
        key: row.__placeholder ? `p-${typeId}-${row.option_id}-${Math.random()}` : `r-${row.data_id}`,
        data_id: row.data_id ?? null,
        type_id: Number(typeId),
        option_id: Number(row.option_id),
        option_name: row.option_name || '',
        name: row.name || '',
        amount: row.__placeholder
            ? hasAmount && !isEmptyValue(row.amount) && Number(row.amount) !== 0
                ? row.amount
                : ''
            : Number(row.amount || 0),
        monthly_interest_expense: row.__placeholder
            ? hasMonthly && !isEmptyValue(row.monthly_interest_expense) && Number(row.monthly_interest_expense) !== 0
                ? row.monthly_interest_expense
                : ''
            : Number(row.monthly_interest_expense || 0),
        fieldValues: flattenValues(row.fields || []),
        __placeholder: Boolean(row.__placeholder),
        __autoPolicyLoan: Boolean(row.__autoPolicyLoan)
    };
};
const hydrate = () => {
    deletedIds.value = []; Object.keys(errors).forEach((k) => delete errors[k]);
    drafts.value = props.types.flatMap((type) => buildDisplayRows(type.id, props.rows, props.optionsByType[type.id] || [], props.seedPlaceholders).map((row) => makeDraft(row, type.id)));
};
watch(() => [props.rows, props.types, props.optionsByType, props.seedPlaceholders], hydrate, { immediate: true, deep: true });
const columns = computed(() => [0, 1].map((classType) => ({ classType, title: classType ? '負債／月支出' : '資產／月收入', types: props.types.filter((t) => Number(props.classMap[t.id]) === classType) })));
const forType = (id) => drafts.value.filter((d) => Number(d.type_id) === Number(id));
const touched = isTouchedDraft;
const filled = computed(() => drafts.value.filter(touched));
const totals = computed(() => calculateFlatTotals(
    filled.value.map((draft) => ({ ...draft, __placeholder: false })),
    props.classMap
));
const sumToolOpen = ref(false);
const selectedSumCells = ref([]);
const cellKey = (d, field) => `${d.key}:${field}`;
const toggleSumCell = (d, field) => {
    if (!sumToolOpen.value) return;
    const key = cellKey(d, field);
    selectedSumCells.value = selectedSumCells.value.includes(key)
        ? selectedSumCells.value.filter((x) => x !== key)
        : [...selectedSumCells.value, key];
};
const isSumCellSelected = (d, field) => selectedSumCells.value.includes(cellKey(d, field));
const selectedSum = computed(() => selectedSumCells.value.reduce((result, key) => {
    const [draftKey, field] = key.split(':');
    const draft = drafts.value.find((d) => d.key === draftKey);
    if (!draft || !touched(draft)) return result;
    const value = Number(draft[field]) || 0;
    const sign = classOf(draft) === 1 ? -1 : 1;
    result.count += 1; result.value += sign * value;
    return result;
}, { count: 0, value: 0 }));
const clearSumSelection = () => { selectedSumCells.value = []; };
const money = (v, { signed = false } = {}) => {
    const n = Number(v) || 0;
    const abs = new Intl.NumberFormat('zh-TW').format(Math.abs(n));
    if (!signed) return abs;
    if (n > 0) return `+${abs}`;
    if (n < 0) return `-${abs}`;
    return abs;
};
const signedMoney = (v) => money(v, { signed: true });
/** 負債／支出在合計列以負數呈現（與合計工具一致） */
const signedOutflow = (v) => {
    const n = Number(v) || 0;
    return signedMoney(n > 0 ? -n : n);
};
const updateAmount = (draft, key, event) => {
    const normalized = normalizeFlatAmountInput(event.target.value);
    draft[key] = normalized;
    event.target.value = formatFlatAmountInput(normalized);
    if (classOf(draft) === 1) draft.__autoPolicyLoan = false;
    if (key === 'amount' && classOf(draft) === 0) syncPolicyLoanFromAsset(draft);
};
const monthlyAmortizationOf = (d) =>
    formatMonthlyAmortization(
        calcMonthlyAmortization(d.amount, d.monthly_interest_expense)
    );
const findLiabilityTypeForOption = (optionId) =>
    props.types.find(
        (type) =>
            Number(props.classMap[type.id]) === 1 &&
            (props.optionsByType[type.id] || []).some((o) => oid(o) === Number(optionId))
    );
/** 填寫投資型保單等資產時，自動補對應「保單借款」列（50%、不填利息） */
const syncPolicyLoanFromAsset = (assetDraft) => {
    if (props.readonly || !touched(assetDraft)) return;
    const assetOptionId = Number(assetDraft.option_id);
    const relations = (props.relations || []).filter(
        (relation) =>
            Number(relation.asset_option_id) === assetOptionId &&
            isPolicyLoanOption({
                id: relation.liability_option_id,
                name: relation.liability_option_name ?? relation.liability_option?.name
            })
    );
    if (!relations.length) return;
    const sourceName = String(assetDraft.name || '').trim();
    const suggested = suggestedPolicyLoanAmount(assetDraft.amount);
    relations.forEach((relation) => {
        const liabilityOptionId = Number(relation.liability_option_id);
        const liabilityType = findLiabilityTypeForOption(liabilityOptionId);
        if (!liabilityType) return;
        const optionMeta = (props.optionsByType[liabilityType.id] || []).find(
            (o) => oid(o) === liabilityOptionId
        );
        const existing = drafts.value.find(
            (d) =>
                Number(d.type_id) === Number(liabilityType.id) &&
                Number(d.option_id) === liabilityOptionId &&
                (sourceName
                    ? String(d.name || '').trim() === sourceName
                    : d.__autoPolicyLoan)
        );
        if (existing) {
            if (existing.__autoPolicyLoan && suggested !== '') {
                existing.amount = suggested;
                existing.monthly_interest_expense = '';
            }
            return;
        }
        const draft = makeDraft(
            {
                __placeholder: true,
                __autoPolicyLoan: true,
                option_id: liabilityOptionId,
                option_name: olabel(optionMeta) || relation.liability_option_name || '保單借款',
                name: sourceName || olabel(optionMeta) || '保單借款',
                amount: suggested,
                monthly_interest_expense: ''
            },
            liabilityType.id
        );
        draft.__placeholder = false;
        insertDraft(liabilityType, draft);
    });
};
const subtotal = (id) => forType(id).reduce((n, d) => n + (Number(d.amount) || 0), 0);
const classOf = (d) => Number(props.classMap[d.type_id]);
const amountLabel = (d) => classOf(d) === 1 ? '負債餘額' : '市值（現值）';
const monthlyLabel = (d) => classOf(d) === 1 ? '月支出' : '月收入';
const definitions = (d) => props.fieldsByOption[d.option_id] || [];
const ftype = (f) => f.input_type || f.type || 'string';
const foptions = (f) => (f.type_data || f.detail_status || []).map((x) => ({ value: String(x.id ?? x.value), label: x.name ?? x.label ?? String(x.id ?? x.value) }));
const visible = (f, d) => !f.dependency_field_id || f.dependency_value == null || String(d.fieldValues[Number(f.dependency_field_id)] ?? '').toLowerCase() === String(f.dependency_value).toLowerCase();
const visibleFields = (d) => definitions(d).filter((f) => visible(f, d));
const filledExtrasCount = (d) =>
    Object.values(d.fieldValues || {}).filter((value) => value !== '' && value != null).length;
/** 空白預設列不顯示；名稱有填、有金額／月收支，或已寫過補充欄位才出現。 */
const showExtrasTrigger = (d) => {
    if (!visibleFields(d).length) return false;
    if (touched(d)) return true;
    if (!isEmptyValue(d.amount) || !isEmptyValue(d.monthly_interest_expense)) return true;
    return filledExtrasCount(d) > 0;
};
const extrasIncomplete = (d) =>
    visibleFields(d).length > 0 && filledExtrasCount(d) < visibleFields(d).length;
const hasFieldErrors = (d) =>
    Object.keys(errors[d.key] || {}).some((key) => key.startsWith('f-'));
/** 已啟用的列若補充欄未齊，或驗證已失敗，都當成警示。 */
const extrasWarn = (d) => hasFieldErrors(d) || (touched(d) && extrasIncomplete(d));
const changeOption = (d) => {
    d.option_name = olabel(optionFor(d));
    d.fieldValues = {};
    delete errors[d.key];
};
const modalOpen = ref(false);
const modalTarget = ref(null);
const modalType = ref(null);
const modalForm = reactive({ option_id: 0, name: '', amount: '', monthly_interest_expense: '', fieldValues: {} });
const modalErrors = reactive({});
const resetModalErrors = () => Object.keys(modalErrors).forEach((key) => delete modalErrors[key]);
const seedModal = (type, draft) => {
    modalType.value = type;
    modalTarget.value = draft;
    Object.assign(modalForm, {
        option_id: Number(draft.option_id),
        name: draft.name || '',
        amount: draft.amount ?? '',
        monthly_interest_expense: draft.monthly_interest_expense ?? '',
        fieldValues: { ...(draft.fieldValues || {}) }
    });
    resetModalErrors();
    modalOpen.value = true;
};
const modalDraft = computed(() => ({
    type_id: Number(modalType.value?.id),
    option_id: Number(modalForm.option_id),
    fieldValues: modalForm.fieldValues
}));
const modalVisibleFields = computed(() => visibleFields(modalDraft.value));
const modalFilledCount = computed(() =>
    modalVisibleFields.value.filter((field) => !isEmptyValue(modalForm.fieldValues[field.id])).length
);
const openExtraFields = (type, draft) => seedModal(type, draft);
const confirmModal = () => {
    if (props.readonly) { modalOpen.value = false; return; }
    resetModalErrors();
    modalVisibleFields.value.forEach((field) => {
        const value = modalForm.fieldValues[field.id];
        if (value === '' || value == null) modalErrors[`f-${field.id}`] = `請填寫${field.name}`;
    });
    if (Object.keys(modalErrors).length) return;
    const target = modalTarget.value;
    Object.assign(target, {
        fieldValues: { ...modalForm.fieldValues }
    });
    modalOpen.value = false;
};
const add = (type, raw) => {
    const option = (props.optionsByType[type.id] || []).find((o) => oid(o) === Number(raw));
    if (!option) return;
    insertDraft(
        type,
        makeDraft(
            {
                __placeholder: true,
                option_id: oid(option),
                option_name: olabel(option),
                name: olabel(option)
            },
            type.id
        )
    );
};
const remove = (d) => {
    if (d.data_id) deletedIds.value.push(d.data_id);
    drafts.value = drafts.value.filter((x) => x.key !== d.key);
};
const suggestionRows = computed(() => filled.value.map((d) => ({ ...d, __placeholder: false, option_name: olabel(optionFor(d)) })));
const suggestions = (column, type) => { const ids = new Set((props.optionsByType[type.id] || []).map(oid)); return buildActionableSuggestions(column.classType, type.id, suggestionRows.value, props.relations).filter((s) => ids.has(Number(s.missing_option_id))); };
const insertDraft = (type, draft) => {
    const firstOfType = drafts.value.findIndex((d) => Number(d.type_id) === Number(type.id));
    if (firstOfType < 0) drafts.value.unshift(draft);
    else drafts.value.splice(firstOfType, 0, draft);
};
const applyPolicyLoanDefaults = (draft, suggestion) => {
    if (!isPolicyLoanOption({ id: draft.option_id, name: draft.option_name || suggestion?.missing_option_name })) {
        return;
    }
    const suggested =
        suggestion?.suggested_amount ||
        suggestedPolicyLoanAmount(suggestion?.filled_amount);
    if (suggested !== '') draft.amount = suggested;
    draft.monthly_interest_expense = '';
    draft.__autoPolicyLoan = true;
    if (suggestion?.filled_name) draft.name = suggestion.filled_name;
};
const applySuggestion = (type, s) => {
    const blank = drafts.value.find(
        (d) =>
            Number(d.type_id) === Number(type.id) &&
            Number(d.option_id) === Number(s.missing_option_id) &&
            !touched(d)
    );
    if (blank) {
        blank.name = s.filled_name || s.missing_option_name || blank.name || '';
        blank.option_name = s.missing_option_name || blank.option_name || '';
        applyPolicyLoanDefaults(blank, s);
        blank.__placeholder = false;
        return;
    }
    const draft = makeDraft(
        {
            __placeholder: true,
            option_id: s.missing_option_id,
            option_name: s.missing_option_name,
            name: s.filled_name || s.missing_option_name || ''
        },
        type.id
    );
    applyPolicyLoanDefaults(draft, s);
    draft.__placeholder = false;
    insertDraft(type, draft);
};
const serializeFields = (d) => visibleFields(d).map((f) => ({ field_id: Number(f.id), value: String(d.fieldValues[f.id] ?? ''), fields: [] }));
const validate = () => {
    Object.keys(errors).forEach((k) => delete errors[k]); let first = '';
    filled.value.forEach((d) => { const e = {};
        Object.assign(e, validateFlatDraft(d, optionFor(d), visibleFields(d), { monthly: monthlyLabel(d) }));
        if (Object.keys(e).length) { errors[d.key] = e; if (!first) first = Object.values(e)[0]; }
    }); return first;
};
const save = () => {
    const prepared = prepareSave();
    if (!prepared.ok) return;
    emit('save-all', prepared.payload);
};
const prepareSave = () => {
    const validationError = validate();
    if (validationError) { document.querySelector('.sheet-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return { ok: false, message: validationError }; }
    const clearedIds = drafts.value.filter((d) => d.data_id && !touched(d)).map((d) => d.data_id);
    return { ok: true, payload: { deletedIds: [...new Set([...deletedIds.value, ...clearedIds])], batches: props.types.map((type) => ({ typeId: Number(type.id), data: forType(type.id).filter(touched).map((d) => ({ option_id: Number(d.option_id), name: String(d.name).trim(), amount: Number(d.amount) || 0, monthly_interest_expense: Number(d.monthly_interest_expense) || 0, fields: serializeFields(d) })) })) } };
};
defineExpose({ prepareSave });
</script>

<template>
<section class="sheet" :class="{ 'sum-mode': sumToolOpen, 'sum-mode--readonly': sumCellInteractionMode({ sumToolOpen, readonly }) === 'container' }">
  <div class="sum-tool sum-tool--top"><div class="sum-tool__identity"><span class="sum-tool__icon">Σ</span><div><strong class="sum-tool__title">合計工具</strong><small>點選金額儲存格快速加總</small></div></div><button type="button" class="sum-tool__toggle" :class="{ active: sumToolOpen }" :aria-pressed="sumToolOpen" @click="sumToolOpen=!sumToolOpen;clearSumSelection()"><span class="sum-tool__toggle-dot" aria-hidden="true" /><span>{{ sumToolOpen ? '已開啟' : '點擊開啟' }}</span></button><span v-if="sumToolOpen" class="sum-tool__hint">資產／收入為正，負債／支出為負</span><strong v-if="sumToolOpen">Σ {{ selectedSum.count }} 格：{{ money(selectedSum.value) }}</strong><button v-if="sumToolOpen" type="button" class="sum-tool__clear" @click="clearSumSelection">清除選取</button></div>
  <div class="columns">
    <section v-for="column in columns" :key="column.classType" class="column">
      <header :class="['column-title', column.classType ? 'liability' : 'asset']"><strong>{{ column.title }}</strong><span>{{ column.types.reduce((n,t)=>n+forType(t.id).filter(touched).length,0) }} 項已填</span></header>
      <div :class="['head-row', column.classType ? 'head-row--liability' : 'head-row--asset']">
        <span>分類</span>
        <span>名稱</span>
        <span>{{ column.classType ? '負債餘額' : '市值（現值）' }}</span>
        <span>{{ column.classType ? '月支出' : '月收入' }}</span>
        <span v-if="column.classType">月攤還</span>
        <span class="head-row__act" aria-hidden="true" />
      </div>
      <article v-for="type in column.types" :key="type.id" class="type">
        <header class="type-title">
          <div><strong>{{ type.name }}</strong><a-tooltip v-if="type.hint" :title="type.hint"><span class="tip">ⓘ</span></a-tooltip></div>
          <span>{{ forType(type.id).filter(touched).length }} 項</span><span>小計 {{ money(subtotal(type.id)) }}</span>
          <select v-if="!readonly" value="" @change="add(type,$event.target.value);$event.target.value=''" aria-label="新增項目"><option value="" disabled>＋ 新增</option><option v-for="o in optionsByType[type.id]||[]" :key="oid(o)" :value="oid(o)">{{ olabel(o) }}</option></select>
        </header>
        <div v-for="s in suggestions(column,type)" :key="`${s.filled_option_id}-${s.missing_option_id}-${s.filled_name}`" class="suggestion">
          <span>建議補上「{{ s.missing_option_name }}」，因已填「{{ s.filled_name }}」<template v-if="s.suggested_amount">（預設 {{ formatFlatAmountInput(s.suggested_amount) }}）</template></span>
          <button v-if="!readonly" type="button" @click="applySuggestion(type,s)">加入表格</button>
        </div>
        <div v-for="d in forType(type.id)" :key="d.key" :class="['draft',{pending:!touched(d),'sheet-error':errors[d.key],'extras-warn':extrasWarn(d)}]">
          <div :class="['main-row', classOf(d) === 1 ? 'main-row--liability' : 'main-row--asset']">
            <label class="option-cell"><select v-model.number="d.option_id" :disabled="readonly" aria-label="分類" @change="changeOption(d)"><option v-for="o in optionsByType[d.type_id]||[]" :key="oid(o)" :value="oid(o)">{{ olabel(o) }}</option></select></label>
            <label class="name-cell"><input v-model="d.name" :disabled="readonly" aria-label="名稱" @change="classOf(d)===0 && syncPolicyLoanFromAsset(d)"><small v-if="errors[d.key]?.name">{{ errors[d.key].name }}</small></label>
            <label :class="{ 'sum-selected': isSumCellSelected(d, 'amount'), 'sum-selected--liability': classOf(d) === 1 && isSumCellSelected(d, 'amount') }" @click="toggleSumCell(d, 'amount')"><input :value="formatFlatAmountInput(d.amount)" :disabled="readonly" type="text" inputmode="numeric" :aria-label="amountLabel(d)" @input="updateAmount(d, 'amount', $event)"><small v-if="errors[d.key]?.amount">{{ errors[d.key].amount }}</small></label>
            <label :class="{ 'sum-selected': isSumCellSelected(d, 'monthly_interest_expense'), 'sum-selected--liability': classOf(d) === 1 && isSumCellSelected(d, 'monthly_interest_expense') }" @click="toggleSumCell(d, 'monthly_interest_expense')"><input :value="formatFlatAmountInput(d.monthly_interest_expense)" :disabled="readonly" type="text" inputmode="numeric" :aria-label="monthlyLabel(d)" @input="updateAmount(d, 'monthly_interest_expense', $event)"><small v-if="errors[d.key]?.monthly">{{ errors[d.key]?.monthly }}</small></label>
            <div v-if="classOf(d) === 1" class="amort-cell" title="月支出÷餘額，僅顯示不入庫">
              <span>{{ monthlyAmortizationOf(d) }}</span>
            </div>
            <button
              v-if="!readonly"
              type="button"
              class="remove"
              title="刪除列"
              aria-label="刪除列"
              @click="remove(d)"
            >×</button>
          </div>
          <div v-if="showExtrasTrigger(d)" :class="['extras-trigger-row', { 'is-warn': extrasWarn(d) }]">
            <button
              type="button"
              :class="['extras-trigger', { complete: !extrasWarn(d) && filledExtrasCount(d) >= visibleFields(d).length, warn: extrasWarn(d) }]"
              @click="openExtraFields(type,d)"
            >
              {{ readonly ? '查看補充資訊' : (extrasWarn(d) ? '請補齊補充資訊' : '補充資訊') }}
              <span>{{ filledExtrasCount(d) }}/{{ visibleFields(d).length }}</span>
            </button>
            <strong v-if="extrasWarn(d)" class="extras-warn-msg">尚未填寫完整</strong>
          </div>
        </div>
        <div v-if="shouldShowEmptyCategoryHint(readonly, forType(type.id).length)" class="empty">尚無資料，可由右上方新增。</div>
      </article>
    </section>
  </div>
  <div class="totals"><div><small>總資產</small><strong>{{signedMoney(totals.assets)}}</strong></div><div><small>總負債</small><strong class="red">{{signedOutflow(totals.liabilities)}}</strong></div><div><small>淨資產</small><strong :class="totals.net>=0?'green':'red'">{{signedMoney(totals.net)}}</strong></div><div><small>月收入</small><strong class="green">{{signedMoney(totals.income)}}</strong></div><div><small>月支出</small><strong class="orange">{{signedOutflow(totals.expenses)}}</strong></div><div><small>月現金流</small><strong :class="totals.cashFlow>=0?'green':'red'">{{signedMoney(totals.cashFlow)}}</strong></div></div>
  <div v-if="!readonly && showSaveBar" class="save-bar"><span>空白列不會送出；{{ saveLabel }}後仍可繼續修改，完成後再提交版本。</span><a-button type="primary" class="flat-sheet-save-btn" :loading="saveLoading" @click="save">{{ saveLabel }}</a-button></div>

  <a-modal
    v-model:open="modalOpen"
    :width="420"
    centered
    wrap-class-name="sheet-extras-modal"
    :ok-text="readonly ? undefined : '儲存'"
    :cancel-text="readonly ? '關閉' : '取消'"
    :ok-button-props="readonly ? { style: { display: 'none' } } : { class: 'sheet-extras-ok' }"
    :cancel-button-props="{ class: 'sheet-extras-cancel' }"
    @ok="confirmModal"
  >
    <template #title>
      <div class="sheet-extras-title">
        <span class="sheet-extras-title__mark" aria-hidden="true" />
        <div>
          <div class="sheet-extras-title__name">{{ modalForm.name || '補充資訊' }}</div>
          <div class="sheet-extras-title__meta">
            補充資訊
            <template v-if="modalVisibleFields.length">
              · {{ modalFilledCount }}/{{ modalVisibleFields.length }}
            </template>
          </div>
        </div>
      </div>
    </template>
    <div v-if="modalVisibleFields.length" class="sheet-modal-extras">
      <label
        v-for="f in modalVisibleFields"
        :key="f.id"
        class="sheet-modal-field"
        :class="{ 'is-error': modalErrors[`f-${f.id}`] }"
      >
        <span>{{ f.name }} <a-tooltip v-if="f.hint" :title="f.hint"><b class="tip">ⓘ</b></a-tooltip></span>
        <input
          v-if="['number','int'].includes(ftype(f))"
          v-model.number="modalForm.fieldValues[f.id]"
          :disabled="readonly"
          type="number"
          placeholder="請輸入"
        >
        <select
          v-else-if="ftype(f)==='array'"
          v-model="modalForm.fieldValues[f.id]"
          :class="{ 'is-empty': isEmptyValue(modalForm.fieldValues[f.id]) }"
          :disabled="readonly"
        >
          <option value="" disabled>請選擇</option>
          <option v-for="x in foptions(f)" :key="x.value" :value="x.value">{{ x.label }}</option>
        </select>
        <select
          v-else-if="ftype(f)==='boolean'"
          v-model="modalForm.fieldValues[f.id]"
          :class="{ 'is-empty': isEmptyValue(modalForm.fieldValues[f.id]) }"
          :disabled="readonly"
        >
          <option value="" disabled>請選擇</option>
          <option :value="1">是</option>
          <option :value="0">否</option>
        </select>
        <input v-else v-model="modalForm.fieldValues[f.id]" :disabled="readonly" placeholder="請輸入">
        <small v-if="modalErrors[`f-${f.id}`]">{{ modalErrors[`f-${f.id}`] }}</small>
      </label>
    </div>
    <div v-else class="sheet-modal-empty">此項目沒有補充資訊。</div>
  </a-modal>
</section>
</template>

<style scoped>
.sheet{margin-bottom:20px}.columns{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.column{min-width:0;overflow:hidden;border:1px solid #d0d5dd;border-radius:8px;background:#fff}.column-title{display:flex;justify-content:space-between;padding:10px 12px}.column-title.asset{background:#eaf7ef}.column-title.liability{background:#fff0ec}.column-title span,.type-title>span{color:#667085;font-size:12px}.head-row,.main-row{display:grid;grid-template-columns:minmax(170px,1fr) minmax(95px,.55fr) minmax(95px,.55fr) 32px}.head-row{padding:6px 8px;color:#667085;background:#f9fafb;font-size:11px;font-weight:700}.head-row span:nth-child(2),.head-row span:nth-child(3){text-align:right}.type{border-top:1px solid #d0d5dd}.type-title{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(100px,auto) auto;gap:8px;align-items:center;padding:5px 7px;background:#f2f4f7}.type-title div{display:flex;gap:5px}.type-title select{max-width:105px;padding:3px;border:1px solid #98a2b3;border-radius:4px;background:#fff;font-size:12px}.tip{color:#667085;font-size:13px;cursor:help}.draft{border-top:1px solid #e4e7ec;border-left:3px solid #16a34a}.draft.pending{border-left-style:dashed;border-left-color:#d59b24;background:#fffdf7}.draft.sheet-error{border-left-color:#dc2626;background:#fff7f7}.main-row>label{min-width:0;padding:5px 7px;border-right:1px solid #e4e7ec}.main-row input,.extras input,.extras select{width:100%;min-width:0;height:30px;padding:3px 6px;border:1px solid transparent;border-radius:3px;background:transparent}.main-row input:focus,.extras input:focus,.extras select:focus{border-color:#52a985;background:#fff;outline:none;box-shadow:0 0 0 2px #dff5e8}.name-cell{display:grid}.option,.extras label>span{color:#475467;font-size:10px;font-weight:700}.main-row small,.extras small{display:block;color:#dc2626;font-size:10px}.remove{align-self:center;width:24px;height:24px;margin:auto;border-radius:4px;color:#b42318;background:#fff0ed}.extras{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1px;padding:6px 8px;border-top:1px dashed #d0d5dd;background:#f8fafc}.extras label{display:grid;min-width:0;padding:4px 6px;background:#fff}.suggestion{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 10px;color:#6b5428;background:#faf7f1;font-size:12px;line-height:1.4}.suggestion button{appearance:none;padding:2px 8px;border:0;border-radius:6px;color:#8b6914;background:transparent;font-size:12px;font-weight:600;cursor:pointer;box-shadow:none}.empty{padding:9px;color:#98a2b3;font-size:12px}.totals{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-top:12px;padding:10px;border:1px solid #d0d5dd;border-radius:8px;background:#fff}.totals div{display:flex;flex-direction:column}.totals small{color:#667085}.totals strong{font-size:16px}.green{color:#16a34a}.red{color:#dc2626}.orange{color:#ea580c}.save-bar{position:sticky;z-index:5;bottom:8px;display:flex;align-items:center;justify-content:space-between;margin-top:10px;padding:10px 12px;border:1px solid #b7d8ca;border-radius:8px;background:rgba(244,251,248,.96);box-shadow:0 5px 18px #1018281f}.save-bar span{color:#667085;font-size:12px}@media(max-width:1200px){.columns{grid-template-columns:1fr}.extras{grid-template-columns:repeat(2,1fr)}}@media(max-width:640px){.head-row{display:none}.main-row{grid-template-columns:1fr 1fr 32px}.name-cell{grid-column:1/-1}.extras{grid-template-columns:1fr}.totals{grid-template-columns:repeat(2,1fr)}.save-bar{align-items:stretch;flex-direction:column}}

/* 桌面維持左資產、右負債；每個儲存格仍限制寬度避免撐破欄位。 */
.sheet,
.sheet * {
    box-sizing: border-box;
}
.columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}
.column,
.type,
.draft,
.main-row,
.main-row > label,
.extras,
.extras > label {
    min-width: 0;
    max-width: 100%;
}
.head-row--asset,
.main-row--asset {
    grid-template-columns: minmax(100px, .85fr) minmax(100px, 1fr) minmax(72px, .75fr) minmax(72px, .75fr) 28px;
}
.head-row--liability,
.main-row--liability {
    grid-template-columns: minmax(90px, .75fr) minmax(90px, .9fr) minmax(68px, .7fr) minmax(68px, .7fr) minmax(68px, .7fr) 28px;
}
.head-row__act {
    width: 28px;
}
.amort-cell {
    display: flex;
    min-width: 0;
    min-height: 52px;
    align-items: center;
    justify-content: flex-end;
    padding: 3px 6px;
    border-right: 1px solid #e4e7ec;
    color: #667085;
    font-variant-numeric: tabular-nums;
    font-size: 13px;
}
.main-row > label {
    padding: 3px 6px;
}
.main-row input,
.main-row select,
.extras input,
.extras select {
    display: block;
    max-width: 100%;
    height: 28px;
}
.extras {
    grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr));
    padding: 4px 6px;
}
.extras label {
    padding: 3px 5px;
}
.totals {
    grid-template-columns: repeat(6, minmax(0, 1fr));
}
.cell-label {
    display: block;
    overflow: hidden;
    margin-bottom: 1px;
    color: #667085;
    font-size: 9px;
    font-weight: 650;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.head-row {
    align-items: center;
    min-height: 42px;
    padding-top: 8px;
    padding-bottom: 8px;
    color: #344054;
    background: #eef2f6;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .01em;
}
.head-row span,
.head-row span:nth-child(2),
.head-row span:nth-child(3) {
    text-align: center;
}
.remove {
    display: grid;
    align-self: center;
    justify-self: center;
    width: 24px;
    height: 24px;
    margin: 0;
    padding: 0;
    place-items: center;
    border: 0;
    border-radius: 4px;
    color: #b42318;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    transition: opacity .12s ease, background .12s ease;
}
.draft:hover .remove,
.draft:focus-within .remove,
.remove:focus-visible {
    opacity: 1;
    background: #fff0ed;
}
.main-row > label {
    display: flex;
    min-height: 52px;
    align-items: center;
}
.main-row > label > input {
    height: 34px;
}
.main-row > label > select {
    width: 100%;
    min-width: 0;
    height: 34px;
    padding: 3px 26px 3px 6px;
    border: 1px solid transparent;
    border-radius: 3px;
    color: #344054;
    background-color: transparent;
    font-size: 13px;
    font-weight: 750;
}
.main-row > label > select:focus {
    border-color: #52a985;
    background-color: #fff;
    outline: none;
    box-shadow: 0 0 0 2px #dff5e8;
}
.name-cell {
    display: flex !important;
    flex-wrap: wrap;
}
.name-cell small {
    width: 100%;
}
.suggestion {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 10px;
    border-top: 1px solid #f0ebe3;
    color: #6b5428;
    background: #faf7f1;
    font-size: 12px;
    line-height: 1.45;
}
.suggestion button {
    flex-shrink: 0;
    appearance: none;
    height: 26px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    color: #8b6914;
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: none;
}
.suggestion button:hover {
    background: #efe6d4;
    color: #6b5428;
}
.extras-trigger-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 2px 8px 6px;
    background: transparent;
}
.extras-trigger-row.is-warn {
    justify-content: space-between;
    margin: 0 0 0;
    padding: 8px 10px;
    border-top: 1px solid #fecaca;
    background: linear-gradient(90deg, #fef2f2 0%, #fff7ed 100%);
}
.extras-warn-msg {
    color: #b42318;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
}
.extras-trigger-row > small,
.sheet-modal-field small {
    color: #dc2626;
    font-size: 11px;
}
.extras-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px 3px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #667085;
    background: #f2f4f7;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: color .15s ease, background .15s ease, border-color .15s ease, box-shadow .15s ease;
}
.extras-trigger:hover {
    color: #344054;
    background: #e4e7ec;
}
.extras-trigger span {
    min-width: 28px;
    padding: 1px 6px;
    border-radius: 999px;
    color: #475467;
    background: #fff;
    font-size: 10px;
    font-weight: 700;
    text-align: center;
}
.extras-trigger.complete {
    color: #087443;
    background: #eaf7ef;
}
.extras-trigger.complete span {
    color: #087443;
    background: #fff;
}
.extras-trigger.warn {
    color: #fff;
    border-color: #dc2626;
    background: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18);
}
.extras-trigger.warn:hover {
    color: #fff;
    background: #b91c1c;
}
.extras-trigger.warn span {
    color: #991b1b;
    background: #fff;
}
.draft.sheet-error,
.draft.extras-warn {
    border-left-width: 4px;
    border-left-color: #dc2626 !important;
    background: #fff5f5 !important;
}
.draft.sheet-error.pending,
.draft.extras-warn.pending {
    border-left-style: solid;
}
.sheet-modal-extras {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
}
.sheet-modal-field {
    display: grid;
    min-width: 0;
    gap: 8px;
    padding: 12px 14px;
    border: 1px solid #efe6d4;
    border-radius: 12px;
    color: #475569;
    background: linear-gradient(180deg, #faf7f1 0%, #fff 100%);
    font-size: 13px;
    font-weight: 600;
}
.sheet-modal-field.is-error {
    border-color: #fecaca;
    background: #fff8f8;
}
.sheet-modal-field input,
.sheet-modal-field select {
    width: 100%;
    min-width: 0;
    height: 40px;
    padding: 8px 12px;
    border: 1px solid #e8e2d6;
    border-radius: 10px;
    color: #1f2937;
    background: #fff;
    font-size: 14px;
    font-weight: 500;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.sheet-modal-field select.is-empty {
    color: #94a3b8;
}
.sheet-modal-field input:focus,
.sheet-modal-field select:focus {
    border-color: #c4a35a;
    outline: none;
    box-shadow: 0 0 0 3px rgba(196, 163, 90, 0.16);
}
.sheet-modal-field small {
    color: #dc2626;
    font-size: 12px;
    font-weight: 500;
}
.sheet-modal-empty {
    padding: 12px 0 4px;
    color: #64748b;
    text-align: center;
}
@media(max-width:900px){
    .columns{grid-template-columns:minmax(0,1fr)}
}
@media(max-width:760px){
    .type-title{grid-template-columns:minmax(0,1fr) auto auto}.type-title>span:first-of-type{display:none}
    .head-row{display:none}.main-row{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.option-cell{grid-column:1}.name-cell{grid-column:2}
    .totals{grid-template-columns:repeat(3,minmax(0,1fr))}
}
@media(max-width:480px){.totals{grid-template-columns:repeat(2,minmax(0,1fr))}.save-bar{align-items:stretch;flex-direction:column}}
</style>

<style>
.sheet-extras-modal .ant-modal-content {
    overflow: hidden;
    border: 1px solid #efe6d4;
    border-radius: 16px;
    box-shadow: 0 18px 48px rgba(107, 84, 40, 0.16);
}
.sheet-extras-modal .ant-modal-header {
    margin: 0;
    padding: 16px 20px 14px;
    border-bottom: 1px solid #f0ebe3;
    background: linear-gradient(180deg, #faf7f1 0%, #fff 100%);
}
.sheet-extras-modal .ant-modal-title {
    width: 100%;
}
.sheet-extras-title {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}
.sheet-extras-title__mark {
    width: 3px;
    height: 28px;
    margin-top: 2px;
    border-radius: 99px;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
}
.sheet-extras-title__name {
    color: #1f2937;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
}
.sheet-extras-title__meta {
    margin-top: 2px;
    color: #8b6914;
    font-size: 12px;
    font-weight: 600;
}
.sheet-extras-modal .ant-modal-body {
    padding: 16px 20px 8px;
}
.sheet-extras-modal .ant-modal-footer {
    margin-top: 0;
    padding: 12px 20px 16px;
    border-top: 1px solid #f0ebe3;
    background: #fffcf7;
}
.sheet-extras-modal .sheet-extras-ok.ant-btn-primary {
    height: 36px;
    border-color: #8b6914;
    border-radius: 10px;
    color: #fffdf8;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(139, 105, 20, 0.22);
}
.sum-tool { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:12px; padding:10px 12px; border:1px solid #d8dee8; border-radius:10px; background:linear-gradient(90deg,#f8fafc,#fff); color:#667085; font-size:12px; box-shadow:0 2px 8px rgba(16,24,40,.06); }
.sum-tool button { padding:5px 11px; border:1px solid #c5ccd8; border-radius:6px; color:#344054; background:#fff; cursor:pointer; transition:.15s ease; }
.sum-tool button:hover { border-color:#52a985; background:#f0faf4; }
.sum-tool strong { padding:4px 9px; border-radius:6px; color:#344054; background:#eef2f6; font-variant-numeric:tabular-nums; }
.sum-selected { background:#eaf7ef !important; outline:2px solid #16a34a; outline-offset:-2px; }
.sum-selected--liability { background:#fff1f1 !important; outline-color:#dc2626; }
.sum-selected--liability input { color:#b42318; font-weight:700; }
.sum-mode .main-row > label:nth-child(3), .sum-mode .main-row > label:nth-child(4) { cursor:pointer; }
.sum-mode--readonly .main-row > label:nth-child(3) input, .sum-mode--readonly .main-row > label:nth-child(4) input { pointer-events:none; cursor:pointer; }
.sum-tool__identity { display:flex; align-items:center; gap:9px; margin-right:4px; }
.sum-tool__icon { display:grid; width:30px; height:30px; place-items:center; border-radius:8px; color:#fff; background:linear-gradient(135deg,#52a985,#257953); font-size:19px; font-weight:800; }
.sum-tool__title { display:block; color:#344054; font-size:13px; }
.sum-tool__identity small { display:block; margin-top:1px; color:#98a2b3; font-size:11px; }
.sum-tool__toggle { display:inline-flex; align-items:center; gap:7px; height:30px; padding:0 10px 0 7px !important; border-color:#cbd5e1 !important; border-radius:999px !important; color:#667085 !important; background:#fff !important; font-size:12px; font-weight:700; }
.sum-tool__toggle-dot { width:16px; height:16px; border-radius:50%; background:#cbd5e1; transition:.18s ease; }
.sum-tool__toggle.active { border-color:#52a985 !important; color:#087443 !important; background:#eaf7ef !important; }
.sum-tool__toggle.active .sum-tool__toggle-dot { background:#16a34a; box-shadow:0 0 0 3px #16a34a2b; }
.sum-tool__hint { color:#667085; }
.sum-tool__clear { color:#667085 !important; background:transparent !important; }
.sum-tool--top { position:fixed; z-index:40; right:24px; bottom:24px; max-width:min(560px,calc(100vw - 32px)); margin:0; }
@media (max-width:640px) { .sum-tool--top { right:16px; bottom:16px; left:16px; max-width:none; } .sum-tool__hint { flex-basis:100%; } }
.sheet-extras-modal .sheet-extras-cancel.ant-btn-default {
    height: 36px;
    border-color: #e8e2d6;
    border-radius: 10px;
    color: #4a453c;
    background: #fff;
}
</style>
